// must support both server and client components (because MediaItem is used in both server and client components)

import Image, { getImageProps } from "next/image";
import { ImageType, isAvailable, LinkType } from "@/types/baseTypes.ts";
import styles from "./MediaItem.module.scss";
import { MediaFormatType } from "@/types/ModuleOptionsType";
import { generateMediaFormat } from "@/utils/layout/mediaFormat.ts";
import type { ComponentProps, CSSProperties } from "react";
import MediaInfo from "@/components/MediaInfo/MediaInfo";
import { CustomLink } from "../CustomLink";
import { customSanitize } from "@/utils/customSanitize";
import { CustomSVG } from "../_primitives/CustomSVG/CustomSVG.client";
import { MediaItemImage } from "./MediaItemImage";

interface MediaItemProps {
  image_fullhd?: ImageType | false;
  image_desktop: ImageType | false;
  image_tablet?: ImageType | false;
  image_mobile?: ImageType | false;
  sizes: string | undefined;
  quality?: number;
  mediaFormat?: MediaFormatType;
  showPlayIcon?: boolean;
  priority?: boolean;
  className?: string;
  copyrightPosition?: "top" | "bottom";
  showCopyright?: boolean;
  objectFit?: "cover" | "contain";
  link?: LinkType;
  linkProps?: ComponentProps<"a">;
  disablePrefetch?: boolean;
  hasOverlay?: boolean;
  overlayStyle?: CSSProperties;
  figcaption?: string;
  caption_position?: "has-text-left" | "has-text-centered" | "has-text-right";
  showMediaInfo?: boolean;
  infoButtonPosition?: "top" | "bottom";
}

export type ImageProps = ReturnType<typeof generateImageProps>["rest"];

const generateImageProps = ({
  image,
  sizes,
  quality,
  priority = false,
}: {
  image?: ImageType | false;
  sizes?: string;
  quality: number;
  priority?: boolean;
}) => {
  if (!image) return { srcSet: undefined, rest: undefined };

  const blurDataURL =
    isAvailable<ImageType>(image) && image.image_blur && image.mime_type
      ? `data:${image.mime_type};base64,${image.image_blur}`
      : undefined;

  const {
    props: { srcSet: srcSet, ...rest },
  } = getImageProps({
    width: image.width,
    height: image.height,
    quality,
    src: image.url,
    alt: image.alt || image.title,
    blurDataURL: blurDataURL,
    placeholder: blurDataURL ? "blur" : undefined,
    sizes: sizes,
    priority,
  });

  return { srcSet, rest: rest };
};

const sameUrl = (a?: ImageType | false, b?: ImageType | false) =>
  !!a && !!b && a.url === b.url;

const dedupeAgainstDesktop = ({
  image_fullhd,
  image_desktop,
  image_tablet,
  image_mobile,
}: {
  /* jscpd:ignore-start */
  image_fullhd?: ImageType | false;
  image_desktop: ImageType | false;
  image_tablet?: ImageType | false;
  image_mobile?: ImageType | false;
  /* jscpd:ignore-end */
}) => {
  if (!image_desktop) {
    return { image_fullhd, image_desktop, image_tablet, image_mobile };
  }

  if (sameUrl(image_desktop, image_fullhd)) image_fullhd = false;
  if (sameUrl(image_desktop, image_tablet)) image_tablet = false;
  if (sameUrl(image_desktop, image_mobile)) image_mobile = false;

  return { image_fullhd, image_desktop, image_tablet, image_mobile };
};

export const MediaItem = ({
  image_fullhd,
  image_desktop,
  image_tablet,
  image_mobile,
  sizes,
  quality = 90,
  mediaFormat,
  /*copyrightPosition,*/
  showPlayIcon = false,
  priority = false,
  className = "",
  /*showCopyright,*/
  objectFit = "cover",
  link,
  linkProps,
  disablePrefetch,
  hasOverlay,
  overlayStyle,
  figcaption,
  caption_position,
  showMediaInfo = true,
  infoButtonPosition = "bottom",
}: MediaItemProps) => {
  const deduped = dedupeAgainstDesktop({
    image_fullhd,
    image_desktop,
    image_tablet,
    image_mobile,
  });

  image_fullhd = deduped.image_fullhd;
  image_desktop = deduped.image_desktop;
  image_tablet = deduped.image_tablet;
  image_mobile = deduped.image_mobile;

  // Safety guard: no image at all
  const baseImage =
    image_desktop || image_fullhd || image_tablet || image_mobile;

  if (!baseImage) {
    return null;
  }
  const isSVG = Boolean(baseImage.mime_type?.includes("svg"));

  const { srcSet: fullhdSrcSet, rest: fullHdRest } = generateImageProps({
    image: image_fullhd,
    sizes,
    quality,
    priority,
  });

  const { srcSet: desktopSrcSet, rest: desktopRest } = generateImageProps({
    image: image_desktop,
    sizes,
    quality,
    priority,
  });
  const { srcSet: tabletSrcSet } = generateImageProps({
    image: image_tablet,
    sizes,
    quality,
    priority,
  });
  const { srcSet: mobileSrcSet } = generateImageProps({
    image: image_mobile,
    sizes,
    quality,
    priority,
  });

  const imageProps =
    desktopRest ||
    fullHdRest ||
    generateImageProps({ image: baseImage, sizes, quality, priority }).rest;

  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  function isExactlyOneDefined(...objs: any[]) {
    const definedObjects = objs.filter((obj) => obj);
    // Check if exactly one object is defined
    if (definedObjects.length === 1) {
      return definedObjects[0];
    }
    return;
  }

  const onlyOneImage = isExactlyOneDefined(
    image_fullhd,
    image_desktop,
    image_tablet,
    image_mobile,
  );

  const fetchPriority = imageProps?.fetchPriority;
  if (fetchPriority && imageProps) {
    delete imageProps.fetchPriority;
  }
  const hasMobile = !!image_mobile;
  const hasTablet = !!image_tablet;
  const hasFullhd = !!image_fullhd;

  const fullhdMedia = hasFullhd ? "(min-width: 1408px)" : undefined;
  const desktopMedia =
    hasTablet || hasMobile ? "(min-width: 1024px)" : undefined;
  const tabletMedia = hasMobile ? "(min-width: 768px)" : undefined;

  const ImageJSX = ({ overwriteSizes }: { overwriteSizes?: string }) =>
    onlyOneImage ? (
      <Image
        src={onlyOneImage.url}
        alt={onlyOneImage.alt || onlyOneImage.title}
        sizes={overwriteSizes ?? sizes}
        quality={quality}
        height={onlyOneImage.height}
        width={onlyOneImage.width}
        priority={priority}
        style={{ objectFit: objectFit, width: "100%", height: "100%" }}
      />
    ) : (
      <picture>
        {image_fullhd && (
          <source
            media={fullhdMedia}
            srcSet={fullhdSrcSet ?? image_fullhd.url}
            width={image_fullhd.width}
            height={image_fullhd?.height}
            sizes={overwriteSizes ?? sizes}
          />
        )}
        {image_desktop && (
          <source
            media={desktopMedia}
            srcSet={desktopSrcSet ?? image_desktop.url}
            width={image_desktop.width}
            height={image_desktop.height}
            sizes={overwriteSizes ?? sizes}
          />
        )}
        {image_tablet && (
          <source
            media={tabletMedia}
            srcSet={tabletSrcSet ?? image_tablet.url}
            width={image_tablet.width}
            height={image_tablet.height}
            sizes={overwriteSizes ?? sizes}
          />
        )}
        {image_mobile && (
          <source
            srcSet={mobileSrcSet ?? image_mobile.url}
            width={image_mobile.width}
            height={image_mobile.height}
            sizes={overwriteSizes ?? sizes}
          />
        )}
        <MediaItemImage
          fetchPriority={fetchPriority}
          imageProps={imageProps}
          objectFit={objectFit}
        />
      </picture>
    );
  const classNameString = `image-wrapper ${styles["image-wrapper"]} ${className} ${mediaFormat && !isSVG ? generateMediaFormat(mediaFormat) : ""}`;
  const modalWrapperClasses = `image-wrapper ${styles["image-wrapper"]} ${className}`;

  return (
    <figure className={classNameString}>
      {hasOverlay && (
        <div className={styles["overlay"]} style={overlayStyle}></div>
      )}
      {ImageJSX({})}
      {link && link.url && (
        <CustomLink
          href={link ? link.url : undefined}
          target={link ? link.target : "_self"}
          {...(link["aria_label"] && { "aria-label": link["aria_label"] })}
          disablePrefetch={disablePrefetch}
          className="full-absolute-hidden-link"
          {...linkProps}
        >
          Link
        </CustomLink>
      )}

      {showPlayIcon && (
        <>
          <div aria-hidden={true} className={styles["has-gradient"]}>
            {" "}
          </div>
          <div aria-hidden={true} className={styles["play-icon"]}>
            <CustomSVG
              iconKey="play"
              wrapper={"span"}
              width={16}
              height={16}
              alt="play"
              className="is-flex"
            />
          </div>
        </>
      )}

      {/* <Copyright
        {...images}
        copyrightPosition={copyrightPosition}
        showCopyright={showCopyright}
      />*/}

      {showMediaInfo && (
        <MediaInfo
          image_desktop={image_desktop}
          image_fullhd={image_fullhd}
          image_tablet={image_tablet}
          image_mobile={image_mobile}
          media={
            <figure className={modalWrapperClasses}>
              {ImageJSX({ overwriteSizes: "100vw" })}
            </figure>
          }
          infoButtonPosition={infoButtonPosition}
        />
      )}
      {figcaption && (
        <figcaption
          className={`caption ${caption_position} pt-4`}
          dangerouslySetInnerHTML={{ __html: customSanitize(figcaption) }}
        ></figcaption>
      )}
    </figure>
  );
};
